/**
 * IntegrationsHealthContext (iter51d)
 *
 * Mantiene en background el estado de integraciones del sistema (Resend, MH,
 * Firmador, Mongo). Se refresca cada 5 minutos y al volver a foco la pestaña.
 * Solo corre para super administradores de plataforma; para tenants normales
 * siempre devuelve `null`.
 *
 * Consumidores:
 *   - Sidebar: muestra mini-pulso rojo si overall != 'ok'.
 *   - Otros componentes pueden leer `useIntegrationsHealth()` y mostrar el
 *     estado donde necesiten.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { dteApi } from "../lib/api";
import { useAuth } from "./AuthContext";

const Ctx = createContext({ data: null, loading: false, refresh: () => {} });

const REFRESH_MS = 5 * 60 * 1000; // 5 minutos

export function IntegrationsHealthProvider({ children }) {
  const { user } = useAuth();
  const isPlatformAdmin = (user?.global_role || "").toLowerCase() === "super_admin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const tickRef = useRef(null);

  const fetchOnce = useCallback(async () => {
    if (!isPlatformAdmin) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const r = await dteApi.integrationsStatus();
      setData(r);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    fetchOnce();
    if (!isPlatformAdmin) return;
    tickRef.current = setInterval(fetchOnce, REFRESH_MS);
    const onFocus = () => fetchOnce();
    window.addEventListener("focus", onFocus);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [isPlatformAdmin, fetchOnce]);

  return (
    <Ctx.Provider value={{ data, loading, refresh: fetchOnce }}>
      {children}
    </Ctx.Provider>
  );
}

export const useIntegrationsHealth = () => useContext(Ctx);
